import {
  Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChild,
  ViewChildren
} from '@angular/core';
import { PAOperation } from "../../../classes/operation";
import {
  faArrowRight,
  faCar,
  faCheck, faCheckCircle, faCodeBranch,
  faCog, faExclamationTriangle, faLock, faLockOpen, faPhone, faTrash,
  faUtensils,
  faWindowClose,
  faWrench
} from "@fortawesome/free-solid-svg-icons";
import { PAUtil } from "../../../classes/util";
import { PAProject } from "../../../classes/project";
import { PATour } from "../../../classes/tour";
import { PopupMenuComponent } from "../../../reuseable-components/popup-menu-component/popup-menu.component";
import { PAStore } from "../../../classes/store";
import { PopoutWindowComponent } from "../../../reuseable-components/popout-window/popout-window.component";
import { PADataControl } from "../../../singletons/pa-data-control";
import { PAStoreControl } from "../../../singletons/pa-store-control";
import { PATimeControl } from "../../../singletons/pa-time-control";
import { PAFilterControl } from "../../../singletons/pa-filter-control";
import { PASettingsControl } from "../../../singletons/pa-settings-control";
import { MapBoxService } from "../../../../../_services/mapbox.service";
import { MapContainer } from "../../../map/pa-map";
import { PATechnicianDate } from "../../../classes/technician-date";
import { NgClass, NgIf, NgFor } from '@angular/common';
import { TravelTechnicianMenuComponent } from '../../../reuseable-components/travel-technician-menu/travel-technician-menu.component';
import { TooltipModule } from '@cloudfactorydk/ng2-tooltip-directive';

import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { CommonModule } from "@angular/common";
import { FaIconComponent, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ThrobberComponent } from "../../../../_shared/throbber/throbber.component";
import { TechnicianDateCollection } from "../../../classes/technician-date-collection";

type WarningType = 'Holiday' | 'Absence'

@Component({
  selector: 'hawk-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss', './../../../styles/common_styles.scss'],
  standalone: true,
  imports: [NgClass, NgIf, TravelTechnicianMenuComponent, TooltipModule, NgFor, FaIconComponent, PopupMenuComponent, ReactiveFormsModule, MatButton, FormsModule, ThrobberComponent, CommonModule, FontAwesomeModule]
})
export class TimelineComponent implements OnInit, OnChanges {

  @Input() tour?: PATour
  @Input() compareTour: PATour
  @Input() tourIsUpdating: boolean
  @Input() selected: boolean = false
  @Input() showHours: boolean = false
  @Input() showLabel: boolean = false
  @Input() showStatistics: boolean = false
  @Input() showWarnings: boolean = false
  @Input() showTimeMarker: boolean = false
  @Input() editable: boolean = false
  @Input() timeFilter: string = ''
  @Input() externalWindow: PopoutWindowComponent
  @Input() chainOperations: boolean
  @Input() height: number = 20
  @Input() showOperationIndexes = true
  @Input() showEverySecondHour = false
  @Input() unfittingOperations: PAOperation[] = []
  @Input() maxTourTime: number
  @Input() addBottomPaddingPxAmount = 0
  @Input() mapContainer: MapContainer
  @Input() technicianDateCollection: TechnicianDateCollection

  @Output() selectTourEvent: EventEmitter<PATour> = new EventEmitter<PATour>()
  @Output() removeOperationIdsEvent: EventEmitter<number[]> = new EventEmitter<number[]>()
  @Output() updateNewTourEvent: EventEmitter<void> = new EventEmitter<void>()

  @ViewChildren('OperationPopups') popupMenus: QueryList<PopupMenuComponent>;
  @ViewChild('LunchBreakPopup') lunchBreakPopup: PopupMenuComponent;
  @ViewChild('WarningsPopup') warningsPopup: PopupMenuComponent;
  @ViewChild('timeline') timeline: ElementRef<HTMLDivElement>;

  protected readonly faCar = faCar;
  protected readonly faCheck = faCheck;
  protected readonly Operation = PAOperation;
  protected readonly faWrench = faWrench;
  protected readonly faArrowRight = faArrowRight;
  protected readonly faWindowClose = faWindowClose;
  protected readonly faUtensils = faUtensils;
  protected readonly faCog = faCog;
  protected readonly Math = Math;
  protected readonly Util = PAUtil;
  protected readonly Project = PAProject;

  timelineTimestamps: { time_string: string, offset_percent: string }[]
  outsideOpeningZones: { left_percent: number, right_percent: number, info: string, operation: PAOperation }[]

  dragAndDropOperations: PAOperation[] = []
  dragAndDropOffsetPercent: number = 0
  selectedOperation: PAOperation

  minuteMarkerPercentage = null

  warning: {type: WarningType, info: string} = null
  showCompareTour: boolean;

  constructor(
    public mapBoxLoadingService: MapBoxService
  ) {
  }

  ngOnInit(): void {
    this.updateTimelineTimestamps()
    this.updateMinuteMarker()
    setInterval(() => {
      this.updateMinuteMarker()
    }, 60000);
    this.updateOutsideOpeningZones()
    this.generateWarning()
  }

  ngOnChanges(changes: SimpleChanges): void {
    let tour_changes = changes['tour']
    let updating_changes = changes['tourIsUpdating']
    if ((tour_changes && tour_changes.currentValue) || (updating_changes && !updating_changes.currentValue)) {
      this.updateOutsideOpeningZones()
    }

    let compare_tour_changes = changes['compareTour']
    if (compare_tour_changes && !compare_tour_changes.currentValue) {
      this.showCompareTour = false
    }
  }

  generateWarning(): void {
    if (this.tour) {
      const public_holidays = this.tour.technicianDate.getPublicHolidays()
      const local_holidays = this.tour.technicianDate.getLocalHolidays()
      const absences = this.tour.technicianDate.absences
      if (public_holidays.length) {
        this.warning = {
          type: 'Holiday',
          info: 'Feiertag: ' + public_holidays[0].name
        }
      } else if (absences.length) {
        this.warning = {
          type: "Absence",
          info: 'Abwesenheit'
        }
        const reason_id = absences[0].reasonId
        PADataControl.Instance.absenceCalendar.waitForReason(reason_id).then(
          _ => {
            this.warning = {
              type: "Absence",
              info: 'Abwesenheit: ' + PADataControl.Instance.absenceCalendar.reasonMap.get(reason_id).name
            }
          }
        )

      } else if (local_holidays.length && !this.tour.technicianDate.tour.operations.length) {
        this.warning = {
          type: 'Holiday',
          info: 'Mögl. regionaler Feiertag: ' + local_holidays[0].name
        }
      }
    }
  }

  openOperationMenu(idx: number): void {
    let popup_components = this.popupMenus.toArray()
    for (let popup_component of popup_components) {
      if (popup_components.indexOf(popup_component) == idx) {
        popup_component.open()
      } else {
        popup_component.close()
      }
    }
  }

  openLunchBreakMenu(): void {
    this.lunchBreakPopup.open()
  }

  openWarningsMenu(): void {
    if (this.tour) {
      let has_warning_operations = this.tour.technicianDate.changedTourContainer.deletingOperations.length + this.tour.technicianDate.changedTourContainer.unfittingOperations.length
      if (!this.tour.technicianDate.isPastTechnicianDate && has_warning_operations) {
        this.warningsPopup.open()
      }
    }
  }

  closeWarningsMenu(): void {
    this.warningsPopup.close()
  }

  closeOperationMenu(idx: number): void {
    this.popupMenus.toArray()[idx].close();
  }

  updateTimelineTimestamps(): void {
    let res: { time_string: string, offset_percent: string }[] = []
    let start_int = Math.floor(PATour.timelineStart)
    let end_int = Math.ceil(PATour.timelineEnd)
    let total_hours = end_int - start_int
    let hourly_offset = 100 / total_hours
    let shown_hours = Array.from(Array(end_int - start_int + 1).keys()).map(x => x + start_int)
    for (let hour of shown_hours) {
      let hour_index = shown_hours.indexOf(hour)
      if (hour_index % 2 == 0 || !this.showEverySecondHour) {
        res.push({time_string: hour.toString(), offset_percent: `${hour_index * hourly_offset}%`})
      }
    }
    this.timelineTimestamps = res
  }

  initDragAndDropCalculation(event: MouseEvent, clicked_operation: PAOperation): void {
    if (this.editable) {
      if (!clicked_operation.date_travel_start && !clicked_operation.date_on_site && !clicked_operation.date_finished && !clicked_operation.ticket.appointment_date) {
        var start = 0, diff = 0;
        if (event.pageX) start = event.pageX;
        else if (event.clientX) start = event.clientX;

        if (!event.ctrlKey) {
          if (!this.dragAndDropOperations.includes(clicked_operation)) {
            this.dragAndDropOperations = [clicked_operation]
            this.selectedOperation = clicked_operation
            let base_operation = PADataControl.Instance.getOperation(clicked_operation.id)
            PAStoreControl.Instance.selectStore(base_operation.getStore(), {show_operation: base_operation})
          }
          if (this.dragAndDropOperations.length <= 1) {
            this.openOperationMenu(this.tour.operations.indexOf(clicked_operation))
          }
        } else {
          if (!this.dragAndDropOperations.includes(clicked_operation)) {
            this.dragAndDropOperations.push(clicked_operation)
            this.openOperationMenu(this.tour.operations.indexOf(clicked_operation))
          } else if (this.dragAndDropOperations.length > 1) {
            PAUtil.removeElementFromList(this.dragAndDropOperations, clicked_operation)
          }
        }

        if (!event.ctrlKey) {
          let timeline_elem = this.timeline.nativeElement
          let mousemove_elem = document
          if (timeline_elem) {
            let timeline_width = timeline_elem.offsetWidth
            mousemove_elem.onmousemove = (e) => {
              var end = 0;
              if (e.pageX) end = e.pageX;
              else if (e.clientX) end = e.clientX;

              diff = end - start;
              this.dragAndDropOffsetPercent = (diff / timeline_width) * 100
              this.updateOutsideOpeningZones()
            };
            mousemove_elem.onmouseup = () => {
              if (!this.dragAndDropOffsetPercent) {
                this.dragAndDropOperations = [clicked_operation]
                this.selectedOperation = clicked_operation
                let base_operation = PADataControl.Instance.getOperation(clicked_operation.id)
                PAStoreControl.Instance.selectStore(base_operation.getStore(), {show_operation: base_operation})
                this.openOperationMenu(this.tour.operations.indexOf(clicked_operation))
              }
              if (this.dragAndDropOffsetPercent) {
                this.applyDragAndDropOffsetToOperations(this.chainOperations ? [...this.tour.operations] : this.dragAndDropOperations)
              }
              mousemove_elem.onmousemove = mousemove_elem.onmouseup = null;
            };
          }
        }
      } else {
        this.openOperationMenu(this.tour.operations.indexOf(clicked_operation))
      }
    }
  }

  async applyDragAndDropOffsetToOperations(operations: PAOperation[]): Promise<void> {
    let start_int = Math.floor(PATour.timelineStart)
    let end_int = Math.ceil(PATour.timelineEnd)
    let total_milliseconds = (end_int - start_int) * 60 * 60 * 1000
    let offset_in_milliseconds = (this.dragAndDropOffsetPercent / 100) * total_milliseconds
    await this.applyMillisecondShiftToOperations(offset_in_milliseconds, operations)
  }

  async applyMillisecondShiftToOperations(ms: number, operations: PAOperation[]): Promise<void> {
    let shiftable_operations = operations.filter(op => !op.ticket.appointment_date && !op.started)
    let new_tour_route_before = [...this.tour.operations || []]
    let tours_to_update: PATour[] = []
    for (let operation of shiftable_operations) {
      await operation.setOffOperationByMilliseconds(ms)
      operation.dragDropOffsetFactor = 0
      let tour = operation.getTour()
      if (tour) {
        tours_to_update.push(tour)
      }
    }
    this.dragAndDropOffsetPercent = 0
    shiftable_operations.map(operation => operation.dragDropOffsetFactor = 1)
    for (let tour of [...new Set(tours_to_update)]) {
      tour.updateTourMarkers()
      let { equal_operation_order } = tour.compareRoute(new_tour_route_before)
      if (tour == this.tour && !equal_operation_order) {
        tour.fireUpdateManually('route_order_changed')
      }
    }

    this.updateOutsideOpeningZones()
    // TODO: check if Map updates
  }

  updateOutsideOpeningZones(): void {
    if (this.tour) {
      this.outsideOpeningZones = this.tour.getOutsideOpeningZonesTimelinePositions({
        for_user_with_id: this.tour.technicianDate.technician.id,
        offset_percent: this.dragAndDropOffsetPercent,
        offset_operations: this.chainOperations ? [...this.tour.operations] : this.dragAndDropOperations
      })
    }
  }

  updateMinuteMarker(): void {
    let new_marker_percentage = null
    if (this.tour?.technicianDate.isTodaysTechnicianDate && this.dayMinutesAreInTimeLineRange()) {
      new_marker_percentage = this.getDayMinutesTimeLinePercent()
    }
    this.minuteMarkerPercentage = new_marker_percentage
  }

  dayMinutesAreInTimeLineRange(): boolean {
    let day_minutes_start = PATour.timelineStart * 60
    let day_minutes_end = PATour.timelineEnd * 60

    let local_day_timestamp = PATimeControl.Instance.dateToTimestamp(new Date(), true, false)
    let local_day_minutes = (Date.now() - local_day_timestamp) / (1000 * 60)
    return day_minutes_start <= local_day_minutes && local_day_minutes <= day_minutes_end
  }

  getDayMinutesTimeLinePercent(): number {
    let day_minutes_start = PATour.timelineStart * 60
    let day_minutes_end = PATour.timelineEnd * 60
    let total_minutes = day_minutes_end - day_minutes_start
    let local_day_timestamp = PATimeControl.Instance.dateToTimestamp(new Date(), true, false)
    let local_day_minutes = (Date.now() - local_day_timestamp) / (1000 * 60)

    return ((local_day_minutes - day_minutes_start) / total_minutes) * 100
  }

  async shiftSelectedOperationTimes(event: Event, reference_operation: PAOperation, reference_time_category: string): Promise<void> {
    const input = event.target as HTMLInputElement
    const time: string = input.value

    let new_tour = this.tour
    let operation_index = new_tour ? new_tour.route.operations.indexOf(reference_operation) : -1
    let time_strings = new_tour.route.time_specific_data[PAFilterControl.Instance.selectedOperationTimeFilter].operation_data[operation_index].operation_time_strings
    const minutes_before = PATimeControl.Instance.timeStringToMinutes(time_strings[reference_time_category])
    const minutes_now = PATimeControl.Instance.timeStringToMinutes(time)
    const delta_minutes = minutes_now - minutes_before

    this.applyMillisecondShiftToOperations(delta_minutes * 60 * 1000, this.chainOperations ? [...this.tour.operations] : this.dragAndDropOperations)
  }

  getNewToursOperationPriorityCountsBeforeOperation(operation: PAOperation) {
    let found_count = this.tour.operationPriorityCountsBeforeOperation.find(pcb => pcb.operation.id == operation.id)
    return found_count ? found_count.count : -1
  }

  lunchBreakOffsetPercent(): number {
    return (this.chainOperations ? this.tour.operations : this.dragAndDropOperations).find(
      op => this.tour.operations.findIndex(
        tour_op => tour_op.id == op.id
      ) == this.tour.lunchBreak?.after_operation_idx
    ) ? this.dragAndDropOffsetPercent : 0
  }

  toggleLunchBreakCalculation(technician_date: PATechnicianDate): void {
    if (technician_date.automaticLunchBreakCalculation) {
      technician_date.lunchBreakAfterOperationNumber = (technician_date.tour.lunchBreak?.after_operation_idx ?? 0) + 1
      technician_date.automaticLunchBreakCalculation = false
    } else {
      technician_date.lunchBreakAfterOperationNumber = null
      technician_date.automaticLunchBreakCalculation = true
    }
    this.updateNewTourEvent.emit()
  }

  removeOperation(remove_operation: PAOperation, menu_idx: number): void {
    if (!remove_operation.hasStarted()) {
      this.closeOperationMenu(menu_idx)
      this.removeOperationIdsEvent.emit([remove_operation.id])
    }
  }

  protected readonly faLock = faLock;
  protected readonly faLockOpen = faLockOpen;
  protected readonly PAStore = PAStore;
  protected readonly Number = Number;
  protected readonly PAFilterControl = PAFilterControl;
  protected readonly PASettingsControl = PASettingsControl;
  protected readonly PADataControl = PADataControl;
  protected readonly PAUtil = PAUtil;
  protected readonly PATimeControl = PATimeControl;
  protected readonly faExclamationTriangle = faExclamationTriangle;
  protected readonly faTrash = faTrash;
  protected readonly PAStoreControl = PAStoreControl;
  protected readonly faCodeBranch = faCodeBranch;

  async deleteTour() {
    if (this.tour) {
      let removable_ids = this.tour.operations.filter(
        op => !op.hasStarted()
      ).concat(this.tour.technicianDate.changedTourContainer.unfittingOperations).map(op => op.id)
      this.removeOperationIdsEvent.emit(removable_ids)
    }
  }

  toggleShowCompareTour() {
    if (this.compareTour) {
      this.showCompareTour = !this.showCompareTour
    } else {
      this.showCompareTour = false
    }
  }

  toggleAutomaticTravelDestinationOptimization() {
    if (this.technicianDateCollection) {
      this.technicianDateCollection.toggleAutomaticTravelDestinationOptimizationForTimestamp(this.tour.technicianDate.day.utc_timestamp, true)
    }
  }

  public eventToNumber(event: Event): number {
    const input = event.target as HTMLInputElement

    return Number.parseInt(input.value)
  }

  toggleOperationRemovability(operation: PAOperation) {
    if (!operation.hasStarted()) {
      this.tour.technicianDate.insertionConfigMap.get(operation.id).removable = !this.tour.technicianDate.insertionConfigMap.get(operation.id).removable; this.updateNewTourEvent.emit()
    }
  }

  protected readonly faCheckCircle = faCheckCircle;
  protected readonly faPhone = faPhone;
}
